import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronDown,
  faChevronUp,
  faSearch,
  faLocationDot,
  faLock,
} from "@fortawesome/pro-solid-svg-icons";
import { faUser } from "@fortawesome/pro-regular-svg-icons";
import { useCallback, useState, useRef, useEffect, CSSProperties } from "react";
import cx from "classnames";
import { ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";
import { useAuth } from "react-oidc-context";
import userApi from "../../api/user/userApi";
import debounce from "lodash/debounce";
import UserSearchCriteria from "../../types/dtos/generic/UserSearchCriteria";
import UserSearchDto from "../../types/dtos/generic/UserSearchDto";
import UserSearchApiResponseDataDto from "../../types/dtos/generic/UserSearchApiResponseDataDto";
import Avatar from "./Avatar";
import { t } from "i18next";
import { Badge } from ".";

interface PeoplePickerProps {
  /** The onChange event, for handling state changes.
   * For single-select mode, the value is either a number (the Id of the user) or a UserSearchDto (controlled by the `nonMultiSelectChangeValueType` prop).
   * For multi select it's only an array of DTOs (or null).
   */
  onPeoplePickerChange(
    newValue: number | UserSearchDto | UserSearchDto[] | null,
  ): void;
  initialInput?: string;
  /** The Id attribute for the input element, to match the Label */
  inputId?: string;
  /** The css class names to apply */
  className?: string;
  /** Bool that allows multiple people to be selected, if false just one person */
  allowMultipleSelection?: boolean;
  /** Only needed for when multiple selections is enabled */
  selectedUsers?: UserSearchDto[];
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  nonMultiSelectChangeValueType?: "NUMERIC-ID" | "USER-DTO";
  excludeLoggedInUserFromResults?: boolean;
  userIdToExcludeFromResults?: number | null; // This is used for the admin user details page so the admin doesn't see the user in the results
  autoSuggestModifyWidth?: boolean;
  allowClearField?: boolean;
  onClearFieldClick?(): void;
  /** Function to trigger when input is empty from typing (rather than hitting clear 'x') */
  onInputEmpty?(): void;
  /** Whether to show the selected users list below the input */
  showSelectedUsers?: boolean;
  /** Header text to show above the selected users list */
  selectedUsersHeader?: string;
  /** Primary color for styling elements like hover and focus states */
  clientPrimaryColor?: string;
  /** Optional label to display */
  label?: string;
  /** Whether to show border around the input */
  noBorder?: boolean;
  /** Placeholder text to show when no selection is made */
  placeholder?: string;
  /** Array of user IDs that are locked and cannot be selected (e.g., users who already have a role assigned) */
  lockedMemberIds?: number[];
  /** Message to display when hovering over a locked user */
  lockedMemberTooltip?: string;
  /** Optional extra classes for the trigger control */
  triggerClassName?: string;
  /** Optional inline styles for the trigger control */
  triggerStyle?: CSSProperties;
  /** When false, search is not limited to the user's brand access (e.g. user group admin). Default true. */
  limitByBrandAccess?: boolean;
}

const PeoplePicker = ({
  onPeoplePickerChange,
  initialInput = undefined,
  inputId = "",
  allowMultipleSelection = false,
  selectedUsers = [],
  showValidationErrors = false,
  validationResult = null,
  nonMultiSelectChangeValueType = "NUMERIC-ID",
  excludeLoggedInUserFromResults = false,
  userIdToExcludeFromResults = null,
  allowClearField,
  onClearFieldClick,
  onInputEmpty = undefined,
  clientPrimaryColor = "#3B82F6", // Default to blue if not provided
  label,
  noBorder = false,
  placeholder = "Select person...",
  showSelectedUsers = false,
  selectedUsersHeader = "Selected users",
  lockedMemberIds = [],
  lockedMemberTooltip = "This user already has an assignment. Edit the existing assignment instead.",
  triggerClassName,
  triggerStyle,
  limitByBrandAccess = true,
}: PeoplePickerProps) => {
  const auth = useAuth();
  const apiUsers = new userApi(auth.user?.access_token);
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    UserSearchDto[]
  >([]);
  const [input, setInput] = useState(
    initialInput != undefined ? initialInput : "",
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialInput != undefined) {
      setInput(initialInput);
    }
  }, [initialInput]);

  // Determine display name for the selection
  const selectedUserName =
    !allowMultipleSelection && selectedUsers.length > 0
      ? selectedUsers[0].fullName
      : input;

  const debouncedPerformSearch = useCallback(
    debounce((searchValue: string, userIdsToExclude: number[]) => {
      performSearch(searchValue, userIdsToExclude);
    }, 300),
    [],
  );

  const performSearch = (searchValue: string, userIdsToExclude: number[]) => {
    const criteria: UserSearchCriteria = {
      searchTerm: searchValue,
      selectedUserIdsToExclude: userIdsToExclude,
      excludeLoggedInUser: excludeLoggedInUserFromResults,
      userIdToExclude: userIdToExcludeFromResults,
      limitByBrandAccess,
    };

    apiUsers.getUsersFromSearchCriteria(
      criteria,
      (data: UserSearchApiResponseDataDto) => {
        setFilteredSuggestions(data.users);
      },
      (error) => {
        console.log("User api error", error); // TODO: Handle error properly
      },
    );
  };

  const onChange = (newValue: string) => {
    setInput(newValue);

    if (newValue.length > 0) {
      debouncedPerformSearch(
        newValue,
        selectedUsers.map((x) => x.userId),
      );
    } else if (newValue.length === 0 && onInputEmpty) {
      onInputEmpty();
    }
  };

  const onSuggestionClick = (newValue: UserSearchDto) => {
    if (allowMultipleSelection) {
      const isUserAlreadySelected =
        selectedUsers.findIndex((x) => x.userId === newValue.userId) !== -1;

      // By this point it shouldn't allow a duplicate to be selected as it's filtered out,
      // however for another added level... only add if it's not already been selected
      if (!isUserAlreadySelected) {
        onPeoplePickerChange([...selectedUsers, newValue]);
      }

      setFilteredSuggestions([]);
      setInput("");
      setSearchMode(false);
      setIsExpanded(false); // Close the dropdown after selection in multiple selection mode
    } else {
      setFilteredSuggestions([]);
      setInput(newValue.fullName);
      if (nonMultiSelectChangeValueType === "NUMERIC-ID") {
        onPeoplePickerChange(newValue.userId);
      } else {
        onPeoplePickerChange(newValue);
      }
      setIsExpanded(false);
      setSearchMode(false);
    }
  };

  const onClearField = () => {
    setInput("");
    onPeoplePickerChange(null);

    if (onClearFieldClick) {
      onClearFieldClick();
    }
  };

  const onRemoveFromSelectedUsers = (person: UserSearchDto) => {
    let newSelectedUserState = [...selectedUsers];
    let index = newSelectedUserState.findIndex(
      (x) => x.userId === person.userId,
    );
    newSelectedUserState.splice(index, 1);
    onPeoplePickerChange(newSelectedUserState);
  };

  const startSearch = () => {
    setIsExpanded(true);
    setSearchMode(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const cancelSearch = useCallback(() => {
    debouncedPerformSearch.cancel();
    setSearchMode(false);
    setIsExpanded(false);
    setInput("");
    setFilteredSuggestions([]);
  }, [debouncedPerformSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on trigger or dropdown
      if (
        (triggerRef.current && triggerRef.current.contains(target)) ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return;
      }

      setIsExpanded(false);
      setSearchMode(false);
      setInput("");
      setFilteredSuggestions([]);
    };

    if (isExpanded) {
      // Use capture phase to handle before modal
      document.addEventListener("mousedown", handleClickOutside, true);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside, true);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded || !searchMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      cancelSearch();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isExpanded, searchMode, cancelSearch]);

  return (
    <div className="relative" ref={containerRef}>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}

      {/* Input Control Wrapper */}
      <div className="relative">
        {/* Form Control Trigger */}
        <div
          ref={triggerRef}
          className={cx(
            "px-3 py-2 cursor-pointer transition-colors duration-500",
            noBorder
              ? !triggerClassName && "bg-transparent"
              : "bg-white border border-gray-300 rounded-md hover:border-gray-400",
            !noBorder && isExpanded && "border-primary",
            triggerClassName
          )}
          style={{
            borderColor:
              !noBorder && isExpanded ? clientPrimaryColor : undefined,
            ...triggerStyle,
          }}
          onClick={() => {
            // For multiple selection, always enter search mode when clicking the trigger
            if (allowMultipleSelection) {
              startSearch();
            } else if (!searchMode) {
              startSearch();
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon
                icon={faUser}
                className="text-gray-400 flex-shrink-0"
                size="sm"
              />

              {searchMode ? (
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Type to search..."
                  id={inputId}
                  onChange={(e) => onChange(e.target.value)}
                  value={input}
                  className="bg-transparent border-none focus:outline-none focus:ring-0 w-full p-0 m-0 text-gray-900 text-sm leading-5"
                  autoComplete="off"
                />
              ) : (
                <span
                  className={`${
                    selectedUserName
                      ? "text-gray-900"
                      : "text-gray-400 italic font-light"
                  } p-0 m-0 text-sm leading-5`}
                >
                  {selectedUserName || placeholder}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {!searchMode && allowClearField && selectedUserName && (
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cursor-pointer text-gray-400 hover:text-gray-600"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClearField();
                  }}
                />
              )}

              {!searchMode && (
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  className="text-gray-400"
                  size="sm"
                />
              )}

              {searchMode && (
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cursor-pointer text-gray-400 hover:text-gray-600"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelSearch();
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Dropdown Panel - positioned relative to the input wrapper */}
        {isExpanded && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-md max-h-96 overflow-hidden z-50"
            style={{
              borderRadius: "0.375rem",
              top: "100%",
            }}
          >
            {/* Navigation Header */}
            <div className="bg-gray-50 px-3 py-2.5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {label || "Select Person"}
                </span>
                {!searchMode && (
                  <button
                    className="text-sm flex items-center px-2.5 py-1 rounded-md hover:bg-gray-200 transition-colors"
                    style={{ color: clientPrimaryColor }}
                    onClick={(e) => {
                      e.stopPropagation();
                      startSearch();
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="mr-1.5"
                      size="sm"
                    />
                    <span>Search</span>
                  </button>
                )}
              </div>
            </div>

            {/* Search or Selection Area */}
            {searchMode && (
              <div className="max-h-64 overflow-y-auto">
                {filteredSuggestions.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {input.length > 0
                      ? "No results found"
                      : "Type to search for people"}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filteredSuggestions.map((suggestion) => {
                      const isLocked = lockedMemberIds.includes(
                        suggestion.userId,
                      );
                      return (
                        <li
                          key={suggestion.userId}
                          className={`px-3 py-2 transition-colors ${
                            isLocked
                              ? "bg-gray-100 cursor-not-allowed"
                              : "cursor-pointer hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            !isLocked && onSuggestionClick(suggestion)
                          }
                          title={isLocked ? lockedMemberTooltip : undefined}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <Avatar
                                  userInitials={suggestion.initials}
                                  userFullName={suggestion.fullName}
                                  activeStatus="DO-NOT-SHOW"
                                  imageUrl={suggestion.profileImageUrl}
                                  size={7}
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  {suggestion.fullName}
                                </p>
                                <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faUser}
                                      size="xs"
                                      className="mr-1.5"
                                    />
                                    <span>{t(suggestion.jobTitle)}</span>
                                  </div>
                                  {suggestion.location && (
                                    <>
                                      <span className="mx-1 text-gray-300">
                                        |
                                      </span>
                                      <div className="flex items-center">
                                        <FontAwesomeIcon
                                          icon={faLocationDot}
                                          size="xs"
                                          className="mr-1.5"
                                        />
                                        <span>{suggestion.location}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            {isLocked && (
                              <div
                                className="flex items-center text-gray-500 bg-gray-50 px-2 py-1 rounded-md"
                                title={lockedMemberTooltip}
                              >
                                <FontAwesomeIcon
                                  icon={faLock}
                                  className="mr-1.5 text-gray-400"
                                />
                                <span className="text-xs">
                                  Already assigned
                                </span>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Footer Help */}
            <div className="bg-gray-50 px-3 py-2.5 border-t border-gray-200">
              {searchMode ? (
                <button
                  type="button"
                  onClick={cancelSearch}
                  className="w-full text-xs text-gray-500 text-center hover:text-gray-700 transition-colors cursor-pointer"
                  title="Cancel search"
                >
                  Type to search • Press ESC to cancel
                </button>
              ) : (
                <p className="text-xs text-gray-500 text-center">
                  {allowMultipleSelection && !showSelectedUsers
                    ? "Start typing to search"
                    : "Click search to find people"}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Users Display (Outside Dropdown) */}
      {allowMultipleSelection &&
        showSelectedUsers &&
        selectedUsers.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {selectedUsersHeader}
                </span>
                <Badge
                  text={selectedUsers.length.toString()}
                  backgroundColourClassName="accent-bg"
                  textColourClassName="text-white"
                  marginClassName=""
                />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
              {selectedUsers.map((user, index) => (
                <div
                  key={user.userId}
                  className={`flex items-center justify-between py-2 px-3 ${
                    index !== 0 ? "border-t border-gray-100" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      imageUrl={user.profileImageUrl}
                      userFullName={user.fullName}
                      userInitials={user.initials}
                      size={7}
                    />
                    <div>
                      <p className="font-medium text-gray-700 text-sm">
                        {user.fullName}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-0.5">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={faUser}
                            size="xs"
                            className="mr-1.5"
                          />
                          <span>{t(user.jobTitle)}</span>
                        </div>
                        {user.location && (
                          <>
                            <span className="mx-1 text-gray-300">|</span>
                            <div className="flex items-center">
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                size="xs"
                                className="mr-1.5"
                              />
                              <span>{user.location}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromSelectedUsers(user);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                    aria-label={`Remove ${user.fullName}`}
                    title="Remove user"
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default PeoplePicker;
